// =============================================================================
// src/data/explore-campaign.ts — Perfil de campanha por municipio (DERIVADO).
//
// diverge do golden por decisao de produto 2026-05-31: camada de campanha nova,
//   sem equivalente em unified.html/assets/explore.js. Para nao inventar dado
//   linha-a-linha (e manter coerencia com o dataset), cada perfil e derivado
//   DETERMINISTICAMENTE dos campos ja existentes do municipio (vencedor 2024,
//   eleitorado, IDH). Mesma entrada -> mesma saida; zero aleatoriedade.
//
// SRP: so a derivacao do perfil de campanha. Sem DOM, sem Leaflet.
// =============================================================================

import type {
  MunicipalityId,
  ExploreCampaignProfile,
  ExploreCampaignAlignment,
  ExploreMunicipality,
} from '../app/types';
import { EXPLORE_DATASET } from './explore-dataset';

/** Partidos tratados como base aliada da campanha (vencedor 2024). */
const ALLY_PARTIES: ReadonlySet<string> = new Set(['Avante', 'PSD', 'Republicanos']);
/** Partidos tratados como oposicao (vencedor 2024). */
const OPPOSITION_PARTIES: ReadonlySet<string> = new Set(['PT', 'PL']);

/** Alinhamento a partir do partido vencedor em 2024 (aliado/oposicao/neutro). */
function alignmentOf(winner: string): ExploreCampaignAlignment {
  if (ALLY_PARTIES.has(winner)) return 'aliado';
  if (OPPOSITION_PARTIES.has(winner)) return 'oposicao';
  return 'neutro';
}

/** Faixa 0..3 do eleitorado (em milhares) — escada estavel para reduto/marcadores. */
function eleitoresBand(eleitoresK: number): number {
  return eleitoresK >= 60 ? 3 : eleitoresK >= 25 ? 2 : eleitoresK >= 12 ? 1 : 0;
}

/** Faixa 0..3 do IDH — entra na cobertura de campo. */
function idhBand(idh: number): number {
  return idh >= 0.60 ? 3 : idh >= 0.55 ? 2 : idh >= 0.50 ? 1 : 0;
}

/** Mantem n dentro de 0..3 (baldes de rampa). */
function clamp03(n: number): number {
  return Math.max(0, Math.min(3, n));
}

/** Reduto 0..3: força no eleitorado descontada pela distancia do alinhamento. */
function redutoOf(eleitoresK: number, alinhamento: ExploreCampaignAlignment): number {
  const base = eleitoresBand(eleitoresK);
  if (alinhamento === 'aliado') return base;
  if (alinhamento === 'neutro') return clamp03(base - 1);
  return clamp03(base - 2);
}

/** Cobertura 0..3: media de IDH e eleitorado (gaps de campo onde ambos sao baixos). */
function coberturaOf(idh: number, eleitoresK: number): number {
  return clamp03(Math.floor((idhBand(idh) + eleitoresBand(eleitoresK)) / 2));
}

/** Perfil completo derivado de um municipio do dataset (exportado p/ teste de ramos). */
export function deriveCampaignProfile(mun: ExploreMunicipality): ExploreCampaignProfile {
  /* v8 ignore next -- todo município do dataset tem el['2024']; ?.p ?? '' defensivo p/ noUncheckedIndexedAccess */
  const alinhamento = alignmentOf(mun.el['2024']?.p ?? '');
  const reduto = redutoOf(mun.eleitoresK, alinhamento);
  const cobertura = coberturaOf(mun.idh, mun.eleitoresK);
  const liderancas = Math.min(4, reduto + (alinhamento === 'aliado' ? 1 : 0));
  const doadores = Math.min(4, cobertura + (eleitoresBand(mun.eleitoresK) >= 2 ? 1 : 0));
  return { alinhamento, reduto, cobertura, liderancas, doadores };
}

/** Constroi o mapa pre-computado id -> perfil a partir do dataset estadual. */
function buildCampaignMap(): Record<MunicipalityId, ExploreCampaignProfile> {
  const out: Record<MunicipalityId, ExploreCampaignProfile> = {};
  Object.values(EXPLORE_DATASET.munis).forEach((mun) => { out[mun.id] = deriveCampaignProfile(mun); });
  return out;
}

/** Perfil padrao para ids ausentes (mantem munColor/markers defensivos). */
const NEUTRAL_PROFILE: ExploreCampaignProfile = { alinhamento: 'neutro', reduto: 0, cobertura: 0, liderancas: 0, doadores: 0 };

/** Mapa pre-computado de perfis de campanha por municipio. */
export const EXPLORE_CAMPAIGN: Record<MunicipalityId, ExploreCampaignProfile> = buildCampaignMap();

/** Perfil de campanha de um municipio (fallback neutro se ausente). */
export function campaignProfileOf(id: MunicipalityId): ExploreCampaignProfile {
  return EXPLORE_CAMPAIGN[id] ?? NEUTRAL_PROFILE;
}
